import React from 'react';
import { Text } from '@react-pdf/renderer';

interface SubscriptSymbolProps {
    main: string;
    sub: string;
    secondMain?: string;
}

// Unicode subscript character mapping
const SUBSCRIPT_MAP: Record<string, string> = {
    '0': '₀',
    '1': '₁',
    '2': '₂',
    '3': '₃',
    '4': '₄',
    '5': '₅',
    '6': '₆',
    '7': '₇',
    '8': '₈',
    '9': '₉',
    a: 'ₐ',
    e: 'ₑ',
    h: 'ₕ',
    i: 'ᵢ',
    j: 'ⱼ',
    k: 'ₖ',
    l: 'ₗ',
    m: 'ₘ',
    n: 'ₙ',
    o: 'ₒ',
    p: 'ₚ',
    r: 'ᵣ',
    s: 'ₛ',
    t: 'ₜ',
    u: 'ᵤ',
    v: 'ᵥ',
    x: 'ₓ',
    // Special characters
    '+': '₊',
    '-': '₋',
    '=': '₌',
    '(': '₍',
    ')': '₎',
};

/**
 * Converts a string to Unicode subscript characters.
 * Characters without Unicode subscript equivalents are kept as-is.
 */
export const toUnicodeSubscript = (text: string): string => {
    return text
        .split('')
        .map(char => SUBSCRIPT_MAP[char.toLowerCase()] ?? char)
        .join('');
};

/**
 * Converts a string with underscore notation (e.g., "p_1", "T_req1") to proper subscript format.
 * Handles patterns like "p_1" → "p₁", "T_req1" → "Tᵣₑq₁", "ε_d1" → "εd₁"
 */
export const formatSubscriptString = (text: string): string => {
    // Pattern: split by underscore and convert the part after underscore to subscript
    const parts = text.split('_');
    if (parts.length === 1) return text; // No underscore found

    const mainPart = parts[0];
    const subscriptPart = parts.slice(1).join('_'); // Handle multiple underscores
    return mainPart + toUnicodeSubscript(subscriptPart);
};

/**
 * Checks if a character has a Unicode subscript equivalent
 */
const hasUnicodeSubscript = (char: string): boolean => {
    return char.toLowerCase() in SUBSCRIPT_MAP;
};

/**
 * Checks if all characters in a string have Unicode subscript equivalents
 */
const allCharsHaveUnicodeSubscript = (text: string): boolean => {
    return text.split('').every(char => hasUnicodeSubscript(char));
};

/**
 * PDF subscript style - smaller font positioned lower
 */
const subscriptStyle = {
    fontSize: 7,
    lineHeight: 1,
};

/**
 * Renders a symbol with subscript for PDF documents.
 * Uses Unicode subscripts where available, falls back to styled text for letters without Unicode equivalents.
 */
export const SubscriptSymbol: React.FC<SubscriptSymbolProps> = ({ main, sub, secondMain }) => {
    // If all subscript characters have Unicode equivalents, use them
    if (allCharsHaveUnicodeSubscript(sub)) {
        return (
            <Text>
                {main}
                {toUnicodeSubscript(sub)}
                {secondMain && secondMain}
            </Text>
        );
    }

    // Otherwise, use styled subscript for proper visual appearance
    return (
        <Text>
            {main}
            <Text style={subscriptStyle}>{sub}</Text>
            {secondMain && secondMain}
        </Text>
    );
};

/**
 * Parses a symbol string and returns main part and subscript part.
 * Handles patterns like: γb → (γ, b), CBRbc → (CBR, bc), Ev2sg → (E, v2sg), φ → (φ, ''), cb → (c, b)
 */
export const parseSymbolParts = (symbol: string): { main: string; sub: string } => {
    if (!symbol) return { main: '', sub: '' };

    // Greek letters that commonly appear in symbols
    const greekLetters = ['γ', 'φ', 'α', 'ε', 'σ', 'μ', 'Δ'];

    // Check if starts with Greek letter
    for (const greek of greekLetters) {
        if (symbol.startsWith(greek)) {
            return { main: greek, sub: symbol.slice(greek.length) };
        }
    }

    // Handle patterns like CBRbc, Ev2sg - uppercase/mixed prefix followed by lowercase subscript
    const match = symbol.match(/^([A-Z][A-Za-z0-9]*?)([a-z][a-z0-9]*)$/);
    if (match) {
        return { main: match[1], sub: match[2] };
    }

    // Handle patterns like h"b (h" is main, b is sub)
    const quoteMatch = symbol.match(/^(.+")([a-z]+)$/);
    if (quoteMatch) {
        return { main: quoteMatch[1], sub: quoteMatch[2] };
    }

    // Known single-character main symbols with subscripts (lowercase)
    // e.g., cb (cohesion base), cs (cohesion subbase), rw (wheel radius), pw (tire pressure)
    const knownMainChars = ['c', 'r', 'p', 'h', 's', 'n', 'd', 't', 'q'];
    if (symbol.length >= 2 && knownMainChars.includes(symbol[0])) {
        return { main: symbol[0], sub: symbol.slice(1) };
    }

    // No subscript detected
    return { main: symbol, sub: '' };
};

/**
 * Renders a symbol string as React element with HTML <sub> tag for subscripts.
 * Use this for web UI rendering (not PDF).
 * Examples: γb → γ<sub>b</sub>, CBRbc → CBR<sub>bc</sub>
 */
export const FormatSymbol: React.FC<{ symbol: string }> = ({ symbol }) => {
    const { main, sub } = parseSymbolParts(symbol);

    if (!sub) {
        return <>{symbol}</>;
    }

    return (
        <>
            {main}
            <sub>{sub}</sub>
        </>
    );
};
